// @djsem/ui — Studio App Shell (Canonical UI Prompt 81, visual refresh pass).
//
// Renders the shared shell used 1:1 by web and desktop: grouped left navigation
// (permission/plan gated, section headers from shellModel's `group`), top bar (brand,
// project switcher, search/palette trigger, sync + notification/task counters), main
// content area, and bottom status bar. Command palette overlays on Ctrl/Cmd+K.
//
// All *decisions* come from the pure shellModel — this component only renders. Hover /
// focus-visible / scrollbar styling comes from the injected stylesheet (shellStyles.ts),
// because inline styles can't express pseudo-states.

import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { tokens } from "./tokens";
import { NAV_ICONS, UI_ICONS, FALLBACK_ICON } from "./icons";
import { ensureShellStyles } from "./shellStyles";
import { Badge } from "./components";
import {
  resolveNavigation, filterCommands, deriveStatusBar,
  type ShellUserContext, type ShellStatusInput, type DJSEMNavModuleId, type DJSEMPaletteCommand,
  type ResolvedNavItem,
} from "./shellModel";

/** Props for {@link AppShell}. */
export interface AppShellProps {
  user: ShellUserContext;
  status: ShellStatusInput;
  activeModule: DJSEMNavModuleId;
  onNavigate: (module: DJSEMNavModuleId) => void;
  onCommand?: (command: DJSEMPaletteCommand) => void;
  organisationLabel?: string;
  projectLabel?: string;
  onProjectSwitch?: () => void;
  notificationCount?: number;
  taskCount?: number;
  children: ReactNode;
}

const GROUP_LABEL: Record<string, string> = {
  core: "Music", creative: "Creative", release: "Release", gig: "Perform", system: "System",
};

const S = {
  root: {
    display: "grid", gridTemplateColumns: "212px 1fr", gridTemplateRows: "48px 1fr 28px",
    height: "100vh", background: tokens.color.bg, color: tokens.color.text, fontFamily: tokens.font.sans,
  } as const,
  top: {
    gridColumn: "1 / 3", display: "flex", alignItems: "center", gap: 12, padding: "0 14px",
    borderBottom: `1px solid ${tokens.color.border}`,
    background: `linear-gradient(180deg, ${tokens.color.surface} 0%, ${tokens.color.bg} 100%)`,
  } as const,
  nav: {
    borderRight: `1px solid ${tokens.color.border}`, padding: "6px 8px 6px 0",
    overflowY: "auto", background: tokens.color.bg,
  } as const,
  main: { overflow: "auto", padding: tokens.space(5), background: tokens.color.bg } as const,
  bottom: {
    gridColumn: "1 / 3", display: "flex", alignItems: "center", gap: 16, padding: "0 14px",
    borderTop: `1px solid ${tokens.color.border}`, fontSize: tokens.text.xs, color: tokens.color.muted,
    background: tokens.color.surface, letterSpacing: "0.02em",
  } as const,
};

/** Group nav items in declared order, preserving shellModel's ordering inside each group. */
function groupNav(items: ResolvedNavItem[]): Array<{ group: string; items: ResolvedNavItem[] }> {
  const out: Array<{ group: string; items: ResolvedNavItem[] }> = [];
  for (const item of items) {
    const g = item.adminOnly ? "admin" : (item.group ?? "system");
    const bucket = out.find((b) => b.group === g);
    if (bucket) bucket.items.push(item);
    else out.push({ group: g, items: [item] });
  }
  return out;
}

/**
 * The shared studio shell (Canonical UI Prompt 81) rendered identically by web and desktop:
 * grouped left navigation, top bar, main content area, bottom status bar, and a Ctrl/Cmd+K
 * command palette. All decisions come from the pure shellModel; this component only renders.
 * @param props - See {@link AppShellProps}.
 */
export function AppShell(props: AppShellProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Inject hover/focus styles exactly once per document (idempotent, SSR-safe).
  useEffect(() => { ensureShellStyles(); }, []);

  // Ctrl/Cmd+K opens the palette; Escape closes (prompt 81 §8).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((o) => !o); }
      if (e.key === "Escape") setPaletteOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const nav = useMemo(() => resolveNavigation(props.user), [props.user]);
  const grouped = useMemo(() => groupNav(nav), [nav]);
  const segments = useMemo(() => deriveStatusBar(props.status), [props.status]);
  const commands = useMemo(() => filterCommands(query, props.user), [query, props.user]);

  const runCommand = (cmd: DJSEMPaletteCommand & { planLocked: boolean }) => {
    setPaletteOpen(false);
    setQuery("");
    if (cmd.planLocked) { props.onNavigate("settings"); return; } // route to upgrade path
    props.onNavigate(cmd.module);
    props.onCommand?.(cmd);
  };

  return (
    <div style={S.root} className="djsem-shell">
      <header style={S.top}>
        {/* Brand — serif wordmark matching the landing. */}
        <strong style={{ fontSize: 17, fontFamily: tokens.font.serif, fontWeight: 500, letterSpacing: "0.02em" }}>
          DJ<span style={{ color: tokens.color.accent, fontStyle: "italic" }}>SEM</span>
        </strong>
        <span style={{ fontSize: tokens.text.xs, color: tokens.color.muted, borderLeft: `1px solid ${tokens.color.border}`, paddingLeft: 12 }}>
          {props.organisationLabel ?? "Personal"}
        </span>
        <button className="djsem-topbtn" onClick={props.onProjectSwitch}>
          {props.projectLabel ?? "No project — create or load demo"} <span style={{ fontSize: 9 }}>▾</span>
        </button>
        <input
          className="djsem-search"
          placeholder="Search or run a command…  ⌘K"
          onFocus={() => setPaletteOpen(true)}
          readOnly
        />
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <Badge tone={props.status.online ? "ok" : "warn"}>{props.status.online ? "synced" : "offline"}</Badge>
          <button className="djsem-topbtn" title="Notifications" aria-label="Notifications">
            <span className="djsem-topicon" aria-hidden="true">{UI_ICONS.bell}</span>
            {props.notificationCount ? <span className="djsem-topcount">{props.notificationCount}</span> : null}
          </button>
          <button className="djsem-topbtn" title="Tasks" aria-label="Tasks">
            <span className="djsem-topicon" aria-hidden="true">{UI_ICONS.inbox}</span>
            {props.taskCount ? <span className="djsem-topcount">{props.taskCount}</span> : null}
          </button>
          <button className="djsem-topbtn" title="Account" aria-label="Account">
            <span style={{
              display: "inline-flex", width: 20, height: 20, alignItems: "center", justifyContent: "center",
              borderRadius: "50%", background: tokens.color.surfaceHi, border: `1px solid ${tokens.color.borderHi}`,
              fontSize: 10, color: tokens.color.textDim,
            }}>DJ</span>
          </button>
        </span>
      </header>

      <nav style={S.nav} aria-label="Primary">
        {grouped.map(({ group, items }) => (
          <div key={group}>
            <div className="djsem-nav-group">{GROUP_LABEL[group] ?? group}</div>
            {items.map((item) => {
              const active = item.id === props.activeModule;
              const locked = item.state === "plan_locked";
              return (
                <button
                  key={item.id}
                  className={`djsem-nav-item${locked ? " is-locked" : ""}`}
                  onClick={() => (locked ? props.onNavigate("settings") : props.onNavigate(item.id))}
                  aria-current={active ? "page" : undefined}
                >
                  <span className="djsem-nav-icon" aria-hidden="true">{NAV_ICONS[item.id] ?? FALLBACK_ICON}</span>
                  <span className="djsem-nav-label">{item.label}</span>
                  {locked ? <span title={`Available on the ${item.minPlan} plan`} style={{ fontSize: 10, color: tokens.color.accentDim, marginLeft: "auto" }}>★</span> : null}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <main style={S.main}>{props.children}</main>

      <footer style={S.bottom}>
        {segments.map((seg) => (
          <span key={seg.id} style={{
            color: seg.tone === "danger" ? tokens.color.danger : seg.tone === "warn" ? tokens.color.warn : seg.tone === "ok" ? tokens.color.ok : tokens.color.muted,
            fontWeight: seg.tone === "danger" ? 700 : 400,
          }}>
            {seg.text}
          </span>
        ))}
      </footer>

      {paletteOpen ? (
        <div
          role="dialog" aria-label="Command palette"
          style={{
            position: "fixed", inset: 0, background: "rgba(4,5,8,0.6)", backdropFilter: "blur(3px)",
            display: "flex", justifyContent: "center", paddingTop: 90, zIndex: 100,
          }}
          onClick={() => setPaletteOpen(false)}
        >
          <div
            style={{
              width: 560, maxHeight: 400, overflow: "auto",
              background: tokens.color.surfaceHi, border: `1px solid ${tokens.color.borderHi}`,
              borderRadius: tokens.radius.lg, boxShadow: tokens.shadow.overlay, height: "fit-content",
            }}
            onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}
          >
            <input
              autoFocus
              value={query}
              onChange={(e: { target: { value: string } }) => setQuery(e.target.value)}
              placeholder="Type a command…"
              style={{
                width: "100%", boxSizing: "border-box", background: "transparent", color: tokens.color.text,
                border: "none", borderBottom: `1px solid ${tokens.color.border}`, padding: "13px 16px",
                fontSize: tokens.text.md, fontFamily: tokens.font.sans, outline: "none",
              }}
            />
            {commands.length === 0 ? (
              <div style={{ padding: 18, fontSize: tokens.text.sm, color: tokens.color.muted }}>No matching commands.</div>
            ) : commands.map((cmd) => (
              <button
                key={cmd.id}
                className={`djsem-palette-item${cmd.planLocked ? " is-locked" : ""}`}
                onClick={() => runCommand(cmd)}
              >
                <span>{cmd.title}</span>
                {cmd.planLocked
                  ? <Badge tone="accent">★ {cmd.minPlan}</Badge>
                  : <span style={{ fontSize: tokens.text.xs, color: tokens.color.muted, letterSpacing: "0.05em" }}>{cmd.module}</span>}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
